'use client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { ButtonTab } from '@/app/components/shared/button-tab';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import type { PeriodType, TabItemType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';

export const Tab = ({
  setPeriod,
  period,
  tabList,
}: {
  setPeriod: (val: PeriodType) => void;
  period: PeriodType;
  tabList: TabItemType[];
}) => {
  const { isEligible: isRetentionEligible } = useRetentionStrategy();
  const t = useTranslations();

  const renderTooltip = (item: TabItemType): ReactNode => {
    // 试用 tab 用渐变 TRIAL 徽标替代折扣 tooltip
    if (item.paidTrial) {
      return (
        <div
          className="relative z-10 flex h-[25px] shrink-0 items-center justify-center rounded-6 px-2 py-1 font-medium text-system-white text-xs uppercase italic leading-[1.4]"
          style={{
            backgroundImage:
              'linear-gradient(90deg, #767BFF 0%, #F08DFF 100%), linear-gradient(90deg, #76ADFF 0%, #FFB1CA 100%)',
          }}
        >
          {t('payments.plans.trialBadge')}
        </div>
      );
    }

    if (!item.tooltip) return null;

    const isSubscriptionMonthlyTab = item.value === 'subscription_monthly';

    if (isRetentionEligible && isSubscriptionMonthlyTab && item.isDiscount) {
      return (
        <div className="flex h-[25px] items-center rounded-8 border border-white/30 bg-gradient-to-r from-[#b2d7ff] to-[#ffe3da] px-2 font-medium text-system-black text-xs uppercase italic shadow-[inset_0px_0px_6px_0px_white] sm:hidden">
          {item.tooltip}
        </div>
      );
    }

    return (
      <div className="flex h-[25px] items-center rounded-8 bg-[linear-gradient(90deg,#5765F2_0%,#808BFF_100%)] px-2 font-medium text-system-white text-xs uppercase italic sm:hidden">
        {item.tooltip}
      </div>
    );
  };

  return (
    <ButtonTab
      className="relative my-6 flex h-11 justify-center rounded-[12px] bg-system-white08 p-0 font-normal ring-1 ring-system-white08 sm:mx-4 sm:my-4 sm:w-auto"
      buttonClassName="relative min-w-[200px] rounded-[12px] border border-transparent px-10 py-3 text-sm text-system-white64 gap-2 overflow-hidden aria-selected:text-system-white aria-selected:bg-system-white24 aria-selected:border-system-white08 sm:w-full sm:min-w-0 sm:flex-wrap sm:min-h-[44px] sm:px-3 opacity-100 hover:text-system-white h-11"
      active={period}
      onChange={(val) => {
        const nextPeriod = val as PeriodType;
        if (isRetentionEligible && nextPeriod !== period) {
          reportEvent('subscription_reactivation_cancel_click', { pricing: 'switch_tab' });
        }
        setPeriod(nextPeriod);
      }}
      options={tabList.map((item) => ({
        value: item.value,
        className: item.paidTrial ? 'group gap-2.5 px-12 aria-selected:bg-system-white16' : undefined,
        label: (
          <>
            {item.paidTrial && (
              <span
                aria-hidden="true"
                className="-left-1 pointer-events-none absolute inset-0"
                data-paid-trial-dot-pattern
                style={{
                  backgroundImage: 'radial-gradient(circle, #7E9ECB 0 1px, transparent 1.25px)',
                  backgroundSize: '5px 5px',
                  maskImage: 'linear-gradient(90deg, transparent 45%, rgba(0, 0, 0, 0.25) 60%, #000 88%)',
                  WebkitMaskImage: 'linear-gradient(90deg, transparent 45%, rgba(0, 0, 0, 0.25) 60%, #000 88%)',
                }}
              />
            )}
            <span
              className={cn(
                'relative z-10 truncate leading-[1.4]',
                item.paidTrial &&
                  'bg-gradient-to-r from-[#94C2FF] to-[#CBB3FF] bg-clip-text font-semibold text-transparent transition-opacity group-hover:opacity-100',
                item.paidTrial && (item.value === period ? 'opacity-100' : 'opacity-65'),
              )}
            >
              {item.label}
            </span>
            {renderTooltip(item)}
          </>
        ),
      }))}
    />
  );
};
